import { offerImageSx } from 'components/offers/offerImages';
import { browserDate } from 'utils/time'
import { refreshOfferDefaultImage } from '../../../../useOfferImagePreview';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from 'utils/api';

export const resolveOfferImageUrl = (path) => new URL(path, window.location.origin).href;

// The parent keys this component by business and upload revision to isolate requests.
export default function OfferImageLibrary({ businessId, value, onChange, disabled, dialogZIndex }) {
  const pages = useRef(new Map());
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(0);
  const images = result?.page === page ? result.content : [];
  const hasNext = result?.page === page && result.hasNext;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const deleteImage = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete(`/offer/${businessId}/images/${encodeURIComponent(deleteTarget.fileName)}`);
      refreshOfferDefaultImage(businessId);
      pages.current.clear();
      setResult(null);
      setPage(0);
      setRetry((value) => value + 1);
      setDeleteTarget(null);
    } catch (error) {
      if (['IMAGE_IN_USE', 'IMAGE_REFERENCE_CHECK_FAILED'].includes(error?.code)) {
        setDeleteTarget({ ...deleteTarget, deletable: false, deletionBlockedReason: error.code });
        for (const [pageNumber, cached] of pages.current) {
          pages.current.set(pageNumber, { ...cached, content: cached.content.map((image) =>
            image.url === deleteTarget.url ? { ...image, deletable: false, deletionBlockedReason: error.code } : image) });
        }
        setResult(pages.current.get(page));
      }
      setDeleteError(error?.code === 'IMAGE_IN_USE'
        ? 'This image is used by an offer or draft and can’t be deleted.'
        : 'Couldn’t delete this image. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const cached = pages.current.get(page);
    if (cached) {
      setResult(cached);
      setLoading(false);
      return undefined;
    }
    api.get(`/offer/${businessId}/images?page=${page}&size=4`)
      .then((response) => {
        if (!active) return;
        const next = { ...response, page };
        pages.current.set(page, next);
        setResult(next);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, page, retry]);

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
        {images.map((image, index) => {
          const url = resolveOfferImageUrl(image.url);
          const selected = Boolean(value) && url === resolveOfferImageUrl(value);
          const deleteDisabled = disabled || deleting || selected || image.deletable !== true;
          const deleteTitle = image.deletionBlockedReason === 'IMAGE_IN_USE' ? 'This image is currently used by an offer or draft.'
            : selected ? 'Choose another image before deleting this one.'
            : image.deletable !== true ? 'Couldn’t verify whether this image can be deleted. Reopen the image library to retry.'
            : 'Delete image';
          const date = browserDate(image.modifiedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
          return <Box key={image.url} sx={{ position: 'relative',
            '&:hover .image-delete-control, &:focus-within .image-delete-control': { opacity: 1 } }}><ButtonBase disabled={disabled || deleting} onClick={() => onChange(url)}
            aria-label={`Select image ${index + 1}, ${date}`} aria-pressed={selected}
            sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left', p: 0.5,
              border: '2px solid', borderColor: selected ? 'primary.main' : 'transparent', borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' }, '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
            <Box component="img" src={url} alt="" loading="lazy" sx={{ ...offerImageSx, width: '100%', borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>{date}</Typography>
          </ButtonBase>
            <Tooltip title={deleteTitle} arrow placement="top" describeChild
              PopperProps={{ sx: { zIndex: dialogZIndex == null ? undefined : dialogZIndex + 1 } }}>
              <Box component="span" className="image-delete-control"
                sx={{ position: 'absolute', top: 6, right: 6, transform: 'translate(50%, -50%)',
                  display: 'inline-flex', opacity: 0, '@media (hover: none)': { opacity: 1 } }}>
                <IconButton size="small" color="error" disabled={deleteDisabled}
                  sx={{ width: 20, height: 20, p: 0, bgcolor: 'background.paper', boxShadow: 1,
                    '&:hover': { bgcolor: 'grey.100' },
                    '&.Mui-disabled': { bgcolor: 'background.paper', color: 'text.disabled' } }}
                  aria-label={`Delete image ${index + 1}`}
                  onClick={() => { setDeleteError(''); setDeleteTarget(image); }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
              </Box>
            </Tooltip>
          </Box>;
        })}
      </Box>
      <Dialog sx={{ zIndex: dialogZIndex }} open={Boolean(deleteTarget)} onClose={() => { if (!deleting) setDeleteTarget(null); }}>
        <DialogTitle>Delete image?</DialogTitle>
        <DialogContent>
          <Typography>This permanently deletes the image. Images used by an offer or draft cannot be deleted.</Typography>
          {deleteError && <Typography role="alert" color="error">{deleteError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" disabled={deleting || deleteTarget?.deletable !== true} onClick={deleteImage}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
      {loading && <Typography role="status" variant="caption">Loading images…</Typography>}
      {!loading && !error && images.length === 0 && <Typography variant="caption">No previously uploaded images.</Typography>}
      {error && <Box role="alert">Couldn’t load images. <Button onClick={() => setRetry((n) => n + 1)}>Retry</Button></Box>}
      {(page > 0 || hasNext) && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
        <Button disabled={page === 0 || loading || disabled} onClick={() => setPage((n) => n - 1)}>Previous</Button>
        <Typography variant="caption">Page {page + 1}</Typography>
        <Button disabled={!hasNext || loading || disabled || error} onClick={() => setPage((n) => n + 1)}>Next</Button>
      </Box>}
    </Box>
  );
}
